import { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, X, Save, Image } from 'lucide-react';
import toast from 'react-hot-toast';
import { contentService } from '../../services/contentService';
import { uploadImage } from '../../services/uploadService';
import { imgUrl } from '../../utils/imgUrl';

const CATEGORIES = ['general', 'rooms', 'views', 'nature', 'restaurant'];

const emptyForm = { file: null, previewUrl: '', title: '', category: 'general', sort_order: 0 };

const ManageGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editImage, setEditImage] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    contentService.getGallery()
      .then(res => setImages(res.data.images || []))
      .catch(() => toast.error('Failed to load gallery.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditImage(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (img) => {
    setEditImage(img);
    setForm({ file: null, previewUrl: imgUrl(img.url), title: img.title, category: img.category, sort_order: img.sort_order });
    setError('');
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) setForm(prev => ({ ...prev, file: f, previewUrl: URL.createObjectURL(f) }));
  };

  const handleSave = async () => {
    if (!form.title) { setError('Title is required.'); return; }
    if (!editImage && !form.file) { setError('Please select an image file.'); return; }
    setSaving(true);
    setError('');
    try {
      let url = editImage?.url || '';
      if (form.file) {
        url = await uploadImage(form.file);
      }
      const payload = { url, title: form.title, category: form.category, sort_order: form.sort_order };
      if (editImage) {
        await contentService.updateGalleryImage(editImage.id, payload);
        toast.success('Image updated.');
      } else {
        await contentService.addGalleryImage(payload);
        toast.success('Image added to gallery.');
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    try {
      await contentService.deleteGalleryImage(id);
      setImages(prev => prev.filter(i => i.id !== id));
      toast.success('Image deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete.');
    }
  };

  const handleDelete = (id, title) => {
    toast((t) => (
      <div>
        <p style={{ margin: '0 0 10px', fontWeight: '500' }}>Delete "{title}"?</p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => { toast.dismiss(t.id); doDelete(id); }}
            style={{ padding: '6px 14px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
            Delete
          </button>
          <button onClick={() => toast.dismiss(t.id)}
            style={{ padding: '6px 14px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const handleToggleActive = async (img) => {
    try {
      await contentService.updateGalleryImage(img.id, { is_active: !img.is_active });
      setImages(prev => prev.map(i => i.id === img.id ? { ...i, is_active: !i.is_active } : i));
      toast.success(img.is_active ? 'Hidden from gallery.' : 'Shown in gallery.');
    } catch {
      toast.error('Failed to update.');
    }
  };

  const filtered = filterCat === 'all' ? images : images.filter(i => i.category === filterCat);

  return (
    <div style={s.container}>
      <div style={s.topBar}>
        <div>
          <h1 style={s.pageTitle}>Manage Gallery</h1>
          <p style={s.pageSubtitle}>{images.length} photos</p>
        </div>
        <button style={s.addBtn} onClick={openAdd}><Plus size={18} /> Add Photo</button>
      </div>

      {/* Category filter */}
      <div style={s.filterRow}>
        {['all', ...CATEGORIES].map(cat => (
          <button key={cat} style={{ ...s.filterBtn, ...(filterCat === cat ? s.filterBtnActive : {}) }}
            onClick={() => setFilterCat(cat)}>
            {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading gallery...</p>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>
          <Image size={64} color="#d1d5db" />
          <p style={s.emptyText}>No photos yet. Add your first photo!</p>
        </div>
      ) : (
        <div style={s.grid}>
          {filtered.map(img => (
            <div key={img.id} style={{ ...s.card, opacity: img.is_active ? 1 : 0.5 }}>
              <div style={s.imgWrap}>
                <img src={imgUrl(img.url)} alt={img.title} style={s.img}
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop'; }} />
                <span style={{ ...s.catBadge, ...catColors[img.category] }}>{img.category}</span>
              </div>
              <div style={s.cardBody}>
                <p style={s.imgTitle}>{img.title}</p>
                <p style={s.imgOrder}>Sort: {img.sort_order}</p>
                <div style={s.actions}>
                  <button style={s.toggleBtn} onClick={() => handleToggleActive(img)}>
                    {img.is_active ? 'Hide' : 'Show'}
                  </button>
                  <button style={s.editBtn} onClick={() => openEdit(img)}><Edit2 size={14} /></button>
                  <button style={s.deleteBtn} onClick={() => handleDelete(img.id, img.title)}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>{editImage ? 'Edit Photo' : 'Add Photo'}</h2>
              <button style={s.closeBtn} onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            {error && <div style={s.errorBox}>{error}</div>}

            <div style={s.inputGroup}>
              <label style={s.label}>
                Image {editImage && <span style={{ fontWeight: '400', color: '#9ca3af', fontSize: '12px' }}>(leave empty to keep current)</span>}
              </label>
              <input type="file" accept="image/*" style={{ display: 'none' }} ref={fileRef} onChange={handleFileChange} />
              <button style={s.filePickerBtn} type="button" onClick={() => fileRef.current?.click()}>
                <Image size={16} />
                {form.file ? form.file.name : 'Choose Image'}
              </button>
            </div>

            {form.previewUrl && (
              <div style={{ marginBottom: '16px' }}>
                <img src={form.previewUrl} alt="preview"
                  style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: '8px' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}

            <div style={s.formRow}>
              <div style={{ ...s.inputGroup, flex: 2 }}>
                <label style={s.label}>Title</label>
                <input style={s.input} type="text" placeholder="e.g. Mountain View" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div style={{ ...s.inputGroup, flex: 1 }}>
                <label style={s.label}>Sort Order</label>
                <input style={s.input} type="number" value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
              </div>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Category</label>
              <select style={s.input} value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtnModal} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
                <Save size={16} /> {saving ? 'Uploading...' : 'Save Photo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const catColors = {
  general:    { backgroundColor: '#f3f4f6', color: '#374151' },
  rooms:      { backgroundColor: '#dbeafe', color: '#1d4ed8' },
  views:      { backgroundColor: '#d1fae5', color: '#065f46' },
  nature:     { backgroundColor: '#dcfce7', color: '#15803d' },
  restaurant: { backgroundColor: '#fef3c7', color: '#92400e' },
};

const s = {
  container: { padding: '32px', backgroundColor: '#f9fafb', minHeight: '100vh' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  pageTitle: { fontSize: '32px', fontWeight: 'bold', color: '#111827', marginBottom: '6px' },
  pageSubtitle: { fontSize: '16px', color: '#6b7280' },
  addBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  filterRow: { display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' },
  filterBtn: { padding: '8px 20px', backgroundColor: 'white', color: '#6b7280', border: '2px solid #e5e7eb', borderRadius: '20px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  filterBtnActive: { backgroundColor: '#16a34a', color: 'white', borderColor: '#16a34a' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '20px' },
  card: { backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'opacity 0.2s' },
  imgWrap: { position: 'relative', height: '180px' },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  catBadge: { position: 'absolute', top: '8px', left: '8px', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize' },
  cardBody: { padding: '14px' },
  imgTitle: { fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '2px' },
  imgOrder: { fontSize: '12px', color: '#9ca3af', marginBottom: '10px' },
  actions: { display: 'flex', gap: '8px', alignItems: 'center' },
  toggleBtn: { flex: 1, padding: '6px', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  editBtn: { padding: '6px 10px', backgroundColor: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  deleteBtn: { padding: '6px 10px', backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  empty: { textAlign: 'center', padding: '60px 20px', backgroundColor: 'white', borderRadius: '12px' },
  emptyText: { fontSize: '18px', color: '#9ca3af', marginTop: '16px' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '16px', padding: '32px', width: '520px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  modalTitle: { fontSize: '22px', fontWeight: '600', color: '#111827' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' },
  formRow: { display: 'flex', gap: '16px' },
  inputGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: '10px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Poppins', sans-serif", color: '#1f2937', backgroundColor: '#fff' },
  filePickerBtn: { display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 14px', border: '1.5px dashed #d1d5db', borderRadius: '10px', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '14px', cursor: 'pointer', boxSizing: 'border-box', transition: 'border-color 0.15s, color 0.15s' },
  modalFooter: { display: 'flex', gap: '12px', marginTop: '8px' },
  cancelBtnModal: { flex: 1, padding: '12px', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  saveBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
};

export default ManageGallery;
